import { createContext } from 'react';
import { createRoot } from 'react-dom/client';
import createCache from '@emotion/cache';

import { DEVICE_DESKTOP, DEVICE_MOBILE } from '../constants/device';
import { REACT_APP_VERSION } from '../constants/envs';
import { FONT_FAMILY_KEY } from '../constants/localStorage';
import { DEFAULT_FONT_FAMILY, INHERIT_FONT_FAMILY } from '../constants/lw';

import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';
import { getHashFromString } from '@/mui/helpers';

import { setCustomCssVariables, unmountAllTags } from './dom';
import { getContainerId } from './embedCodeParams';
import { fetchFont } from './fonts';
import { getStorage, setStoreParam } from './localStorage';
import logger from './logger';
import { GlobalIntersectionObserver, GlobalResizeObserver } from './observers';

export const RootContainers = (() => {
  const disconnectedCallBack = { current: () => null };

  let rootContainer = null;

  let hiddenWrapperPlayerDOMContainer = null;

  let appContainer = null;
  let portalContainer = null;

  let watchStylesRoot = null;
  let stylesObserver = null;

  let muiStylesRoot = null;
  let playerStylesRoot = null;

  return {
    render(getReactLayout) {
      const parentBlock = document.getElementById(getContainerId());

      hiddenWrapperPlayerDOMContainer = document.querySelector('watch-player-container');

      if (!hiddenWrapperPlayerDOMContainer) {
        hiddenWrapperPlayerDOMContainer = document.createElement('watch-player-container');
        hiddenWrapperPlayerDOMContainer.style.cssText =
          'position:fixed;top:-100%;left:-100%;visibility:hidden;opacity:0;z-index:-9999';

        document.body.appendChild(hiddenWrapperPlayerDOMContainer);
      }

      if (!customElements.get('watch-application')) {
        class WatchApplication extends HTMLElement {
          constructor() {
            super();
            this.attachShadow({ mode: 'open' });

            this.shadowRoot.innerHTML = `
<player-css></player-css>
<mui-css></mui-css>
<watch-css></watch-css>
<watch-body data-device="${isMobileUserAgent ? DEVICE_MOBILE : DEVICE_DESKTOP}"></watch-body>
<watch-portal data-device="${isMobileUserAgent ? DEVICE_MOBILE : DEVICE_DESKTOP}"></watch-portal>
`;
          }

          // eslint-disable-next-line class-methods-use-this
          disconnectedCallback() {
            if (typeof disconnectedCallBack?.current === 'function') {
              disconnectedCallBack.current();
            }
          }
        }

        customElements.define('watch-application', WatchApplication);
      }

      parentBlock.innerHTML = `<watch-application translate="no" class="notranslate"></watch-application>`;
      parentBlock.style.minWidth = '0';

      rootContainer = parentBlock.querySelector('watch-application');
      rootContainer.setAttribute('dir', 'ltr');

      playerStylesRoot = rootContainer.shadowRoot.querySelector('player-css');
      muiStylesRoot = rootContainer.shadowRoot.querySelector('mui-css');
      watchStylesRoot = rootContainer.shadowRoot.querySelector('watch-css');
      appContainer = rootContainer.shadowRoot.querySelector('watch-body');
      portalContainer = rootContainer.shadowRoot.querySelector('watch-portal');

      const getKey = (el) => {
        let key = '';
        if (el.tagName === 'LINK' && el.href?.split(/\//)?.pop()?.startsWith(REACT_APP_VERSION)) {
          key = el.href;
        } else if (el.tagName === 'STYLE' && (el.getAttribute('data-vite-dev-id') || el.getAttribute('data-lrecss'))) {
          key = el.getAttribute('data-vite-dev-id') || el.innerHTML;
        }

        return getHashFromString(key).toString(36);
      };

      const checkAndAppendTo = () => {
        const watchStyles = Array.from(watchStylesRoot.children || []);
        const playerStyles = Array.from(playerStylesRoot.children || []);
        const cache$ = [...watchStyles, ...playerStyles].reduce(
          (acc, el) => ({
            ...acc,
            [el.getAttribute('data-cache-key')]: true,
          }),
          {},
        );
        const fragments = [];
        const playerFragments = [];
        Array.from(document.head.children || [])
          .filter((el) => !!el?.tagName && ['LINK', 'STYLE'].includes(el.tagName))
          .forEach((el) => {
            const keyKey = getKey(el);

            if (keyKey && !cache$[keyKey]) {
              cache$[keyKey] = true;

              const clonedNode = el.cloneNode(true);
              clonedNode.setAttribute('data-cache-key', keyKey);

              if (el.getAttribute('data-lrecss')) {
                playerFragments.push(clonedNode);
              } else {
                fragments.push(clonedNode);
              }
            }
          });

        fragments.forEach((fragment) => {
          watchStylesRoot.appendChild(fragment);
        });

        playerFragments.forEach((fragment) => {
          playerStylesRoot.appendChild(fragment);
        });
      };

      let windowHeight = null;

      const mainResizeFn = () => {
        const windowHeight$ = window.innerHeight;

        if (windowHeight !== windowHeight$) {
          windowHeight = windowHeight$;

          const { top } = rootContainer.getBoundingClientRect();
          const delta = Math.floor(windowHeight - window.scrollY - top);

          setCustomCssVariables(rootContainer.shadowRoot.host, {
            '--app-height': `${Math.max(0, delta + 1)}px`,
          });
        }
      };

      let { clientWidth } = rootContainer;

      let timer;
      const mainResizeObserverFn = ({ contentRect }) => {
        if (clientWidth !== contentRect.width) {
          clientWidth = contentRect.width;

          clearTimeout(timer);
          timer = setTimeout(() => {
            if (rootContainer) {
              rootContainer.dispatchEvent(new Event('resize'));
            }
          }, 500);
        }
      };

      window.addEventListener('resize', mainResizeFn);
      mainResizeFn();
      GlobalResizeObserver.observe(rootContainer, mainResizeObserverFn);

      // add checking function on length children more then 5
      let portalObserver = null;

      if (process.env.NODE_ENV === 'development') {
        portalObserver = new MutationObserver((mutationsList) => {
          if (mutationsList.some((m) => m.type === 'childList') && portalContainer.children.length > 5) {
            logger.warn(
              'Was rendered to match elements into the portalContainer',
              `size => ${portalContainer.children.length}`,
              'Please check',
            );
          }
        });

        portalObserver.observe(portalContainer, {
          attributes: false,
          childList: true,
          subtree: false,
        });
      }

      const reactRoot = createRoot(appContainer);

      disconnectedCallBack.current = () => {
        window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
        window.removeEventListener('resize', mainResizeFn);
        GlobalIntersectionObserver.disconnect();
        GlobalResizeObserver.disconnect();
        portalObserver?.disconnect();
        unmountAllTags();
        reactRoot.unmount();
      };

      stylesObserver = new MutationObserver(checkAndAppendTo);
      checkAndAppendTo();
      stylesObserver.observe(document.head, { childList: true });

      const cache = createCache({
        key: 'watch-css',
        container: muiStylesRoot,
        prepend: true,
      });

      reactRoot.render(getReactLayout(cache));

      return reactRoot;
    },
    getRootContainer() {
      return rootContainer;
    },
    getMUIPortalContainer() {
      return portalContainer;
    },
    updateCssVariables: (config) => {
      if (![INHERIT_FONT_FAMILY, DEFAULT_FONT_FAMILY].includes(config.font)) {
        const currentFont = getStorage()[FONT_FAMILY_KEY];
        if (currentFont !== config.font) {
          setStoreParam(FONT_FAMILY_KEY, config.font);
          fetchFont();
        }
      }

      setCustomCssVariables(rootContainer.shadowRoot.host, {
        '--sc-title-color': config.cTitleColor,
        '--sc-title-font-size': config.cTitleSize,
        '--layout-popup-size': `${config.popupSize}%`,
        '--sc-desc-color': config.cDescColor,
        '--sc-desc-font-size': config.cDescSize,
        '--sc-vid-title-color': config.cVidTitleColor,
        '--sc-vid-title-font-size': config.cVidTitleSize,
        '--sc-vid-info-color': config.cInfoColor,
        '--hub-offset-top': `${config.offsetTop}px`,
      });
    },
  };
})();

export const IsInPopupContext = createContext(false);

export const getMainWidth = (inPopup, popupSize) =>
  inPopup ? Math.floor((window.innerWidth / 100) * popupSize) : RootContainers.getRootContainer().clientWidth;
